import { Accordion, Button, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconPlayerPlayFilled } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useProjectId } from '~/hooks/context/useProjectId';
import { AUTO_CONCURRENCY } from '~/server/logic/common/workers';
import { api } from '~/trpc-extra/api';
import { queryParamToString } from '~/logic/query-param';
import { parseVariableValue } from '~/common/variable/value';
import CanNotStartTesting from './CanNotStartTesting';
import type { Environment, NotificationProfile } from '@prisma/client';
import { ExecutionOptionsSection } from './ExecutionOptionsSection';
import { ParallelismSection } from './ParallelismSection';
import { NotificationsSection } from './NotificationsSection';
import { EnvironmentSection } from './EnvironmentSection';
import { TestSuiteSkeleton } from './TestSuiteSkeleton';
import type { TestSuiteFetchResult } from '~/server/api/routers/test-suite-router';
import { getCommonSettingsFromSuite } from './form';

export default function ManualTriggerAdvanced({ testSuiteId: incomingTestSuiteId }: { testSuiteId?: string }) {
  const router = useRouter();
  const testSuiteId = incomingTestSuiteId ?? queryParamToString(router.query.testSuiteId);
  const projectId = useProjectId();

  const { data: canRunTests, isLoading: canRunTestsLoading } = api.project.getCanRunTests.useQuery({
    projectId: projectId!,
  });

  const { data: testSuite } = api.testSuite.get.useQuery({
    projectId: projectId!,
    testSuiteId,
  });

  return (
    canRunTestsLoading || !testSuite ? <TestSuiteSkeleton />
    : canRunTests ? <ManualTriggerAdvancedForm testSuite={testSuite} />
    : <CanNotStartTesting />
  );
}

// We pass testSuite to load the data before setting up the form
function ManualTriggerAdvancedForm({ testSuite }: { testSuite: TestSuiteFetchResult }) {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();

  const { mutateAsync: startTestSuite } = api.testSuite.start.useMutation();

  const { data: organizationEmailAddress, isLoading: isLoadingOrganizationEmailAddress } =
    api.stablyEmail.getOrganizationEmailAddress.useQuery({
      projectId: projectId!,
    });
  const orgPrefix = organizationEmailAddress?.split('@').at(0)?.trim() || '';

  const form = useForm({
    initialValues: {
      ...getCommonSettingsFromSuite(testSuite),
      retryOnFailure: 'true',
    },
  });

  const numTests = testSuite?.suiteTests.filter((x) => !x.skip).length;

  return (
    <div className="flex flex-col gap-2">
      <Text>
        Start running{' '}
        <span className="font-semibold">
          {numTests} test{numTests === 1 ? '' : 's'}
        </span>
      </Text>
      <form
        onSubmit={form.onSubmit(
          async ({
            retryOnFailure,
            maxRetries,
            operationDelayMs,
            testTimeoutSeconds,
            numberOfWorkers,
            workerMode,
            notificationProfile,
            environment,
            variableOverrides,
          }) => {
            if (testSuite?.suiteTests.every((x) => x.skip)) {
              notifications.show({
                color: 'red',
                title: 'Unable to run test suite',
                message: `Test suite is empty or all tests are skipped. Add tests to the suite first.`,
                icon: <IconExclamationCircle />,
              });
              modals.closeAll();
              return;
            }

            notifications.show({
              color: 'teal',
              message: `Starting test suite`,
            });
            modals.closeAll();

            try {
              await startTestSuite({
                testSuiteId: testSuite.id,
                options: {
                  [retryOnFailure === 'true' ? 'retries' : 'repeats']: maxRetries,
                  operationDelayMs,
                  testTimeoutMs: testTimeoutSeconds ? testTimeoutSeconds * 1000 : undefined,
                  numberOfWorkers: workerMode === 'manual' ? numberOfWorkers : AUTO_CONCURRENCY,
                  setupTestId: testSuite?.setupTestId ?? undefined,
                  teardownTestId: testSuite?.teardownTestId ?? undefined,
                  beforeEachTestId: testSuite?.beforeEachTestId ?? undefined,
                  afterEachTestId: testSuite?.afterEachTestId ?? undefined,
                  notificationProfile: notificationProfile as NotificationProfile,
                  environment: environment ?? ('NONE' satisfies Environment),
                  variableOverrides: Object.fromEntries(
                    variableOverrides.map(({ name, value, sensitive }) => [
                      name,
                      { value: parseVariableValue(value), sensitive },
                    ]),
                  ),
                },
              });
            } catch (e) {
              console.error(e);
              notifications.show({
                color: 'red',
                title: 'Error',
                message: `${e}`,
                icon: <IconExclamationCircle />,
              });
            }

            await Promise.allSettled([
              trpcUtils.testSuite.getRuns.invalidate({ testSuiteId: testSuite.id }).catch(() => undefined),
              trpcUtils.testSuite.get.invalidate({ projectId, testSuiteId: testSuite.id }).catch(() => undefined),
            ]);
          },
        )}
      >
        <Accordion
          variant="separated"
          multiple
          defaultValue={['execution', 'parallelism']}
        >
          <ExecutionOptionsSection
            allowSelectRetryBehavior={true}
            form={form}
          />

          <ParallelismSection form={form} />

          <EnvironmentSection
            form={form}
            orgPrefix={orgPrefix}
            organizationEmailAddress={organizationEmailAddress}
            isLoadingOrganizationEmailAddress={isLoadingOrganizationEmailAddress}
            variableOverrides={form.values.variableOverrides}
          />

          <NotificationsSection
            form={form}
            projectId={projectId!}
            testSuiteId={testSuite.id}
            notificationProfile={testSuite?.notificationProfile ?? 'PROJECT'}
            hideChannelConfiguration={true}
          />
        </Accordion>
        <div className="mt-2 flex justify-end">
          <Button
            type="submit"
            leftSection={<IconPlayerPlayFilled size={14} />}
          >
            Run {numTests} test{numTests === 1 ? '' : 's'}
          </Button>
        </div>
      </form>
    </div>
  );
}
